import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type TestVariant = 'A' | 'B'

interface ABTest {
  id: string
  name: string
  description: string
  variants: {
    A: { name: string; weight: number }
    B: { name: string; weight: number }
  }
  active: boolean
}

interface ABTestContextType {
  getVariant: (testId: string) => TestVariant
  recordConversion: (testId: string, conversionType: string, metadata?: Record<string, any>) => void
  activeTests: ABTest[]
}

const defaultTests: ABTest[] = [
  {
    id: 'hero_cta_text',
    name: 'Hero CTA Text',
    description: 'Test different call-to-action text on the hero section',
    variants: {
      A: { name: 'Start Free Trial', weight: 50 },
      B: { name: 'Get Started Now', weight: 50 }
    },
    active: true
  },
  {
    id: 'pricing_display',
    name: 'Pricing Display',
    description: 'Test showing monthly vs annual pricing by default',
    variants: {
      A: { name: 'Monthly First', weight: 50 },
      B: { name: 'Annual First', weight: 50 }
    },
    active: true
  },
  {
    id: 'testimonials_count',
    name: 'Testimonials Count',
    description: 'Test showing 3 vs 6 testimonials',
    variants: {
      A: { name: '3 Testimonials', weight: 50 },
      B: { name: '6 Testimonials', weight: 50 }
    },
    active: true
  }
]

const ABTestContext = createContext<ABTestContextType | undefined>(undefined)

interface ABTestProviderProps {
  children: ReactNode
}

export const ABTestProvider: React.FC<ABTestProviderProps> = ({ children }) => {
  const [userVariants, setUserVariants] = useState<Record<string, TestVariant>>({})
  const [activeTests] = useState<ABTest[]>(defaultTests)

  // Load or assign variants on mount
  useEffect(() => {
    const storedVariants = localStorage.getItem('ab_test_variants')
    let variants: Record<string, TestVariant> = {}

    if (storedVariants) {
      try {
        variants = JSON.parse(storedVariants)
      } catch (error) {
        console.warn('Failed to parse stored AB test variants:', error)
      }
    }

    // Assign variants for any tests that don't have assignments
    let needsUpdate = false
    for (const test of activeTests.filter(t => t.active)) {
      if (!variants[test.id]) {
        variants[test.id] = assignVariant(test)
        needsUpdate = true
      }
    }

    if (needsUpdate) {
      localStorage.setItem('ab_test_variants', JSON.stringify(variants))
    }

    setUserVariants(variants)
  }, [activeTests])

  const assignVariant = (test: ABTest): TestVariant => {
    const random = Math.random() * 100
    const weightA = test.variants.A.weight
    return random < weightA ? 'A' : 'B'
  }

  const getVariant = (testId: string): TestVariant => {
    const test = activeTests.find(t => t.id === testId && t.active)
    if (!test) {
      return 'A' // Default to variant A if test doesn't exist or is inactive
    }

    return userVariants[testId] || 'A'
  }

  const recordConversion = (testId: string, conversionType: string, metadata?: Record<string, any>) => {
    const variant = getVariant(testId)
    const test = activeTests.find(t => t.id === testId)
    
    if (!test) return

    // Record the conversion event
    const conversionEvent = {
      testId,
      testName: test.name,
      variant,
      variantName: test.variants[variant].name,
      conversionType,
      metadata,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    }

    // Store in localStorage for now (in production, this would be sent to analytics)
    const storageKey = 'ab_test_conversions'
    const existingConversions = localStorage.getItem(storageKey)
    const conversions = existingConversions ? JSON.parse(existingConversions) : []
    conversions.push(conversionEvent)
    
    // Keep only the last 100 conversions to avoid storage issues
    if (conversions.length > 100) {
      conversions.splice(0, conversions.length - 100)
    }
    
    localStorage.setItem(storageKey, JSON.stringify(conversions))

    // Log for development
    console.log('AB Test Conversion:', conversionEvent)

    // In production, you would also send this to your analytics service
    // analytics.track('ab_test_conversion', conversionEvent)
  }

  return (
    <ABTestContext.Provider
      value={{
        getVariant,
        recordConversion,
        activeTests
      }}
    >
      {children}
    </ABTestContext.Provider>
  )
}

export const useABTest = (): ABTestContextType => {
  const context = useContext(ABTestContext)
  if (context === undefined) {
    throw new Error('useABTest must be used within an ABTestProvider')
  }
  return context
}

// Convenience hook for getting a specific test variant
export const useTestVariant = (testId: string): TestVariant => {
  const { getVariant } = useABTest()
  return getVariant(testId)
}

// Convenience hook for recording conversions with automatic test context
export const useConversion = () => {
  const { recordConversion } = useABTest()
  
  return (testId: string, conversionType: string, metadata?: Record<string, any>) => {
    recordConversion(testId, conversionType, metadata)
  }
}